import { useEffect } from 'react';
import useWebSocketDataStore from '../Store/WebSocketDataStore';
import usePlaybackStore from '../Store/playbackStore';

const EXTRA_INDEX_SYMBOLS = ['ES'];

// The live socket lets a resync request ES history again. SPX history belongs
// to the authenticated hydration service, while this socket still supplies
// live SPX ticks.
let activeSocket = null;

export function sendIndexHistoryRequests(socket) {
  EXTRA_INDEX_SYMBOLS.forEach((symbol) => {
    socket.send(JSON.stringify({ type: 'index-data', symbol }));
  });
}

// Returns false when there is no open socket to ask; the caller reports that
// rather than implying the bars were refreshed. A reconnect requests history on
// its own, so there is nothing to queue.
export function requestIndexHistoryRefresh() {
  if (!activeSocket || activeSocket.readyState !== WebSocket.OPEN) return false;
  try {
    sendIndexHistoryRequests(activeSocket);
    return true;
  } catch {
    return false;
  }
}

const Zero_DTE_Websocket_Client = () => {
 const {
   setIndexData,
   updateIndexData,
   setConnectionStatus,
   recordObservation,
 } = useWebSocketDataStore();

 useEffect(() => {
   let ws = null;
   let reconnectTimeout = null;
   let pingInterval = null;

   const connectWebSocket = () => {
      const wsUrl = process.env.REACT_APP_STREAMING_WEBSOCKET_URL;

     setConnectionStatus('connecting');
     ws = new WebSocket(wsUrl);
     activeSocket = ws;

     // Setup ping interval
     pingInterval = setInterval(() => {
       if (ws && ws.readyState === WebSocket.OPEN) {
         ws.send(JSON.stringify({ type: "ping" }));
       }
     }, 30000);

     ws.onopen = () => {
       // console.log(`Connected to WebSocket server at ${wsUrl}`);
       setConnectionStatus('connected');
       sendIndexHistoryRequests(ws);
     };

     ws.onmessage = (event) => {
       try {
         const response = JSON.parse(event.data);
         // console.log("WebSocket message received:", response);

         // Handle historical data
         if (response.historical && response.symbol) {
           const symbol = response.symbol.replace('I:', '');
           if (symbol === 'SPX') return;
           // console.log(`Processing historical data for ${symbol}`);

           if (!Array.isArray(response.historical) || response.historical.length === 0) {
             // console.warn(`Invalid or empty historical data for ${symbol}:`, response.historical);
             return;
           }

           // Log the entire historical response array
           // console.log(`Full historical response for ${symbol}:`, response.historical);
           // console.log(`Received ${response.historical.length} bars for ${symbol}`);
           // console.log('First bar:', response.historical[0]);
           // console.log('Last bar:', response.historical[response.historical.length - 1]);

           // Store the historical data directly
           setIndexData(symbol, response.historical);

           // A replay running over these bars holds its own projection, cloned
           // when replay was entered. Without this push, history arriving mid-
           // replay -- a reconnect, or a resync asking for it -- reaches the
           // store but never the chart.
           usePlaybackStore.getState().refreshReplaySources();

           // Verify storage
           // const storedData = useWebSocketDataStore.getState().indexData[symbol];
           // console.log(`Stored ${storedData?.length || 0} bars for ${symbol}`);
         }

         // Handle real-time updates
         if (response.realtime_update && response.symbol) {
           const symbol = response.symbol.replace('I:', '');
           // console.log(`Real-time update for ${symbol}:`, response.realtime_update);

           if (symbol === 'SPX') recordObservation(Date.now());
           updateIndexData(symbol, response.realtime_update);

           // Verify state after update
           // const currentState = useWebSocketDataStore.getState().aggregationState[symbol];
           // console.log(`State after real-time update for ${symbol}:`, currentState);
         }
       } catch (error) {
         console.error("Error processing WebSocket message:", error);
         console.error("Raw message:", event.data);
       }
     };

     ws.onclose = (event) => {
       clearInterval(pingInterval);
       setConnectionStatus('disconnected');
       console.log(`WebSocket connection closed (code=${event.code}, reason=${event.reason}). Attempting to reconnect...`);
       scheduleReconnect();
     };

     ws.onerror = (error) => {
       setConnectionStatus('disconnected');
       console.error("WebSocket encountered an error:", error);
     };
   };

   const scheduleReconnect = () => {
     if (reconnectTimeout) return;
     console.log("Scheduling WebSocket reconnection...");
     reconnectTimeout = setTimeout(() => {
       console.log("Reconnecting to WebSocket...");
       connectWebSocket();
       reconnectTimeout = null;
     }, 3000);
   };

   const cleanupWebSocket = () => {
     if (reconnectTimeout) {
       clearTimeout(reconnectTimeout);
       reconnectTimeout = null;
     }

     if (pingInterval) {
       clearInterval(pingInterval);
       pingInterval = null;
     }

     if (ws) {
       console.log("Cleaning up WebSocket connection...");
       if (activeSocket === ws) activeSocket = null;
       ws.onopen = null;
       ws.onmessage = null;
       ws.onclose = null;
       ws.onerror = null;

       if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
         ws.close();
       }
       ws = null;
     }
   };

   connectWebSocket();

   return () => {
     console.log("Component unmounting. Cleaning up WebSocket connection.");
     cleanupWebSocket();
   };
 }, [recordObservation, setConnectionStatus, setIndexData, updateIndexData]);

 return null;
};

export default Zero_DTE_Websocket_Client;















